import React, { useMemo, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import exifr from 'exifr';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_SERVICE_KEY = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
const CORRECT_PIN = process.env.REACT_APP_FOOD_LOG_PIN;

const adminSupabase = createClient(SUPABASE_URL, SUPABASE_SERVICE_KEY);

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function fmtDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

const SOURCE_LABEL = {
  exif: 'EXIF · date taken',
  'exif-create': 'EXIF · created',
  'exif-modify': 'EXIF · modified',
  file: 'file date',
  none: 'no date found',
};

// Pull the best available capture date out of an image's metadata.
// Preference: DateTimeOriginal (shutter time) → CreateDate → ModifyDate →
// the file's own lastModified. EXIF timestamps carry no timezone, so exifr
// hands them back as local-time Dates, which is what ymd() wants.
async function readDate(file) {
  try {
    const meta = await exifr.parse(file, ['DateTimeOriginal', 'CreateDate', 'ModifyDate']);
    if (meta) {
      if (meta.DateTimeOriginal instanceof Date && !isNaN(meta.DateTimeOriginal)) {
        return { date: ymd(meta.DateTimeOriginal), source: 'exif' };
      }
      if (meta.CreateDate instanceof Date && !isNaN(meta.CreateDate)) {
        return { date: ymd(meta.CreateDate), source: 'exif-create' };
      }
      if (meta.ModifyDate instanceof Date && !isNaN(meta.ModifyDate)) {
        return { date: ymd(meta.ModifyDate), source: 'exif-modify' };
      }
    }
  } catch (_) { /* not a parseable image / no EXIF block */ }
  if (file.lastModified) {
    return { date: ymd(new Date(file.lastModified)), source: 'file' };
  }
  return { date: '', source: 'none' };
}

export default function FoodLogMulti() {
  const [screen, setScreen] = useState('pin');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');

  const [rows, setRows] = useState([]); // { id, file, preview, date, source, include }
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [results, setResults] = useState(null); // { done: [{date,url}], failed: [{name,error}] }

  const today = ymd(new Date());

  function handlePin(e) {
    e.preventDefault();
    if (pin === CORRECT_PIN) setScreen('upload');
    else setPinError('Wrong PIN');
  }

  async function handleFileSelect(e) {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setError('');
    setParsing(true);
    const parsed = await Promise.all(
      list.map(async (file, i) => {
        const { date, source } = await readDate(file);
        return {
          id: `${Date.now()}-${i}-${file.name}`,
          file,
          preview: URL.createObjectURL(file),
          date,
          source,
          include: !!date,
        };
      })
    );
    // newest photo first, undated rows sink to the bottom
    parsed.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    setRows(parsed);
    setParsing(false);
  }

  function updateRow(id, patch) {
    setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)));
  }

  function clearFiles() {
    rows.forEach(r => URL.revokeObjectURL(r.preview));
    setRows([]);
    setError('');
  }

  // dates that more than one included row is trying to write — the last
  // upload for a given date wins (Storage upsert), so flag them.
  const dupDates = useMemo(() => {
    const counts = {};
    rows.forEach(r => {
      if (r.include && r.date) counts[r.date] = (counts[r.date] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter(k => counts[k] > 1));
  }, [rows]);

  const includedCount = rows.filter(r => r.include && r.date).length;

  async function handleUpload() {
    const targets = rows.filter(r => r.include && r.date);
    if (!targets.length || uploading) return;
    setUploading(true);
    setError('');
    setProgress({ done: 0, total: targets.length });

    const done = [];
    const failed = [];
    for (const r of targets) {
      try {
        const path = `${r.date}.jpg`;
        const { error: upErr } = await adminSupabase.storage
          .from('food')
          .upload(path, r.file, { contentType: r.file.type, upsert: true });
        if (upErr) throw new Error(upErr.message);
        done.push({ date: r.date, url: `${SUPABASE_URL}/storage/v1/object/public/food/${path}` });
      } catch (err) {
        failed.push({ name: r.file.name, error: err.message });
      }
      setProgress(p => ({ ...p, done: p.done + 1 }));
    }

    done.sort((a, b) => b.date.localeCompare(a.date));
    setResults({ done, failed });
    setScreen('success');
    setUploading(false);
  }

  function reset() {
    rows.forEach(r => URL.revokeObjectURL(r.preview));
    setRows([]);
    setResults(null);
    setProgress({ done: 0, total: 0 });
    setError('');
    setScreen('upload');
  }

  // ── PIN ──────────────────────────────────────────────────
  if (screen === 'pin') {
    return (
      <div className="fl-page">
        <h1 className="fl-title">Lunch Log · Bulk</h1>
        <form className="fl-form" onSubmit={handlePin}>
          <input
            className="fl-pin-input"
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={e => setPin(e.target.value)}
            placeholder="Enter PIN"
            autoFocus
          />
          {pinError && <p className="fl-error">{pinError}</p>}
          <button className="fl-btn" type="submit">Continue</button>
        </form>
        <a className="fl-back" href="/lunch">← Back to diary</a>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────
  if (screen === 'success') {
    const { done, failed } = results;
    const urlBlock = done.map(d => `${d.date}  ${d.url}`).join('\n');
    return (
      <div className="fl-page fl-page-wide">
        <div className="fl-success-mark">✓</div>
        <p className="fl-success-date">
          {done.length} photo{done.length === 1 ? '' : 's'} uploaded
        </p>
        <p className="fl-success-label">
          {done.length ? `${fmtDate(done[done.length - 1].date)} – ${fmtDate(done[0].date)}` : '—'}
        </p>

        {done.length > 0 && (
          <ul className="flm-result-list">
            {done.map(d => (
              <li key={d.date} className="flm-result-row">
                <span className="flm-result-date">{fmtDate(d.date)}</span>
                <a className="flm-result-link" href={d.url} target="_blank" rel="noopener noreferrer">
                  open
                </a>
              </li>
            ))}
          </ul>
        )}

        {failed.length > 0 && (
          <p className="fl-error">
            {failed.length} failed: {failed.map(f => `${f.name} (${f.error})`).join(', ')}
          </p>
        )}

        {done.length > 0 && (
          <>
            <button
              className="fl-btn"
              onClick={() => navigator.clipboard.writeText(urlBlock)}
            >
              Copy all URLs
            </button>
            <p className="fl-hint">Paste the list into Claude to log nutrition for each day</p>
            <a
              className="fl-claude-link fl-btn fl-btn-outline"
              href="https://claude.ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Claude →
            </a>
          </>
        )}

        <button className="fl-btn fl-btn-outline" onClick={reset}>Upload more</button>
        <a className="fl-back" href="/lunch">← Back to diary</a>
      </div>
    );
  }

  // ── Upload / review ──────────────────────────────────────
  return (
    <div className="fl-page fl-page-wide">
      <h1 className="fl-title">Lunch Log · Bulk</h1>

      <label className="fl-photo-area fl-photo-area-multi">
        <span className="fl-photo-hint">
          {rows.length ? `${rows.length} selected — tap to replace` : 'Tap to select photos'}
        </span>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="fl-file-input"
        />
      </label>

      {parsing && <p className="fl-hint">Reading photo dates…</p>}

      {rows.length > 0 && !parsing && (
        <>
          <div className="flm-table">
            {rows.map(r => {
              const isDup = r.include && r.date && dupDates.has(r.date);
              const future = r.date && r.date > today;
              return (
                <div className="flm-row" key={r.id}>
                  <img className="flm-thumb" src={r.preview} alt="" />
                  <div className="flm-meta">
                    <span className="flm-name" title={r.file.name}>{r.file.name}</span>
                    <span className={'flm-source flm-source-' + r.source}>
                      {SOURCE_LABEL[r.source]}
                    </span>
                    <div className="flm-controls">
                      <input
                        className="fl-date-input flm-date-input"
                        type="date"
                        value={r.date}
                        max={today}
                        onChange={e =>
                          updateRow(r.id, { date: e.target.value, source: 'manual', include: !!e.target.value })
                        }
                      />
                      <label className="flm-include">
                        <input
                          type="checkbox"
                          checked={r.include}
                          disabled={!r.date}
                          onChange={e => updateRow(r.id, { include: e.target.checked })}
                        />
                        <span>upload</span>
                      </label>
                    </div>
                    {r.source === 'manual' && <span className="flm-warn">date set manually</span>}
                    {!r.date && <span className="flm-warn">no date — set one to include</span>}
                    {isDup && <span className="flm-warn">duplicate date — last upload wins</span>}
                    {future && <span className="flm-warn">date is in the future</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {error && <p className="fl-error">{error}</p>}

          <button
            className="fl-btn"
            onClick={handleUpload}
            disabled={uploading || includedCount === 0}
          >
            {uploading
              ? `Uploading ${progress.done}/${progress.total}…`
              : `Upload ${includedCount} photo${includedCount === 1 ? '' : 's'}`}
          </button>
          <button className="fl-btn fl-btn-outline" onClick={clearFiles} disabled={uploading}>
            Clear
          </button>
        </>
      )}

      <a className="fl-back" href="/lunch">← Back to diary</a>
    </div>
  );
}
